import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import type { SxProps, Theme, TypographyVariant } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import parse, {
	domToReact,
	Element as DomHandlerElement,
	type HTMLReactParserOptions
} from 'html-react-parser';
import { t } from 'i18next';
import {
	type MouseEvent as ReactMouseEvent,
	ReactNode,
	useCallback,
	useMemo
} from 'react';
import { Link as RouterLink } from 'react-router';

import { BasicChip } from '@/components/Chips/BasicChip';
import { useInlineEditContext } from '@/components/EditableSectionField/InlineEditContext';
import { InlineEditTriggerButton } from '@/components/EditableSectionField/InlineEditTriggerButton/InlineEditTriggerButton';
import { NAME_GROUP_KEY } from '@/core/dcr/fields/inline';
import type { Hcphco } from '@/core/graphql/generated/types';
import { highlightNameMatch } from '@/utils/highlightNameMatch';
import { returnValidIntl } from '@/utils/isValidIntl';
import { mergeSx } from '@/utils/mergeSx';
import { useBreakpoint } from '@/utils/useBreakpoint';

import { useProfileMetaData } from '../HcpCard/_hooks/useProfileMetaData';

import { profileNameStyles as sx } from './ProfileName.styles';

type Props = {
	profile: Hcphco | null;
	withDegrees?: boolean;
	withInternationalName?: boolean;
	withNickname?: boolean;
	withShortName?: boolean;
	withProfileLink?: boolean;
	withInactive?: boolean;
	withLink?: boolean;
	chips?: ReactNode;
	query?: string;

	inline?: boolean;
	shouldValidateMarkup?: boolean;

	noWrap?: boolean;
	wrapChips?: boolean;
	lineClamp?: number;
	variant?: TypographyVariant;
	customSx?: {
		root?: SxProps<Theme>;
		name?: SxProps<Theme>;
	};
};

export const ProfileName = ({
	profile,
	withDegrees,
	withInternationalName,
	withNickname,
	withShortName,
	withProfileLink,
	withInactive = false,
	withLink,
	chips,
	query,

	shouldValidateMarkup = true,
	noWrap = false,
	wrapChips = true,

	inline = false,
	lineClamp,
	variant = 'body1',
	customSx
}: Props) => {
	const { getProfileName, profileLink, internationalName, englishName } =
		useProfileMetaData(profile);
	const isSmall = useBreakpoint('sm', 'down');

	const parseOptions = useMemo<HTMLReactParserOptions>(
		() => ({
			replace: domNode => {
				if (domNode instanceof DomHandlerElement && domNode.name === 'mark') {
					return (
						<Box component='mark' sx={sx.highlightMark}>
							{domToReact(domNode.children as Parameters<typeof domToReact>[0])}
						</Box>
					);
				}
			}
		}),
		[]
	);

	const inlineEditCtx = useInlineEditContext();
	const showNameEdit =
		!!inlineEditCtx?.enabled && profile?.vid_V === inlineEditCtx.entity?.vid_V;

	const openNameEdit = useCallback(
		(e: ReactMouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			inlineEditCtx?.requestActivate(NAME_GROUP_KEY);
		},
		[inlineEditCtx]
	);

	const { name, rawName, degrees } = useMemo(() => {
		const { name: profileName, degrees: profileDegrees } = getProfileName({
			withInternationalName: withInternationalName && inline,
			withShortName,
			withNickname
		});

		const displayName = shouldValidateMarkup
			? highlightNameMatch(profileName, query, profile?.__typename === 'HCO')
			: profileName;

		return {
			name: (shouldValidateMarkup
				? parse(displayName, parseOptions)
				: displayName) as string,
			rawName: profileName,
			degrees: profileDegrees
		};
	}, [
		getProfileName,
		withInternationalName,
		inline,
		withShortName,
		withNickname,
		shouldValidateMarkup,
		query,
		parseOptions,
		profile
	]);

	const internationalNames = useMemo(
		() =>
			[
				returnValidIntl(rawName, internationalName),
				returnValidIntl(rawName, englishName)
			].filter(Boolean),
		[rawName, internationalName, englishName]
	);

	const nameSpan = (
		<Box component='span' sx={sx.nameSpanMargin(withDegrees, inline)}>
			{name}
		</Box>
	);

	const editButton = showNameEdit && profile && (
		<Box component='span' sx={sx.editButtonWrapper}>
			<InlineEditTriggerButton
				onClick={openNameEdit}
				ariaLabel={t('Edit Name')}
				markerAttr='data-edit-icon'
				customSx={{
					desktopButton: sx.editButtonCustomSx,
					iconButton: sx.editIconButtonCustomSx
				}}
			/>
		</Box>
	);

	const content = (
		<Typography
			variant={variant}
			noWrap={noWrap}
			sx={mergeSx(
				customSx?.name,
				!!lineClamp && {
					display: '-webkit-box',
					WebkitLineClamp: lineClamp.toString(),
					WebkitBoxOrient: 'vertical',
					overflow: 'hidden',
					textOverflow: 'ellipsis'
				},
				showNameEdit && sx.editHover
			)}
		>
			{nameSpan}

			<Box component='span' sx={sx.degrees(inline && !wrapChips, !!withLink)}>
				{degrees}
			</Box>

			{wrapChips && (withInactive || chips || showNameEdit) && (
				<Box component='span' sx={sx.chips}>
					{chips}

					{withInactive && profile?.statusCda_VId === 'iact' && (
						<BasicChip label={t('Inactive')} />
					)}

					{editButton}
				</Box>
			)}
		</Typography>
	);

	return (
		<Box sx={mergeSx(sx.root, customSx?.root)}>
			<Box sx={sx.name}>
				{withLink ? (
					<Link component={RouterLink} to={profileLink} underline='none'>
						{content}
					</Link>
				) : (
					content
				)}

				{!wrapChips &&
					(chips ||
						(withInactive && profile?.statusCda_VId === 'iact') ||
						editButton) && (
						<Box component='span' sx={sx.chips}>
							{chips}

							{withInactive && profile?.statusCda_VId === 'iact' && (
								<BasicChip label={t('Inactive')} />
							)}

							{editButton}
						</Box>
					)}

				{withProfileLink &&
					profileLink &&
					(isSmall ? (
						<Tooltip title={t('Open Profile')}>
							<IconButton
								component={RouterLink}
								to={profileLink}
								target='_blank'
								rel='noopener'
								color='primary'
								sx={sx.profileLink}
							>
								<OpenInNewIcon />
							</IconButton>
						</Tooltip>
					) : (
						<Button
							variant='text'
							startIcon={<OpenInNewIcon />}
							component={RouterLink}
							to={profileLink}
							target='_blank'
							rel='noopener'
							sx={sx.profileLink}
						>
							{t('Open Profile')}
						</Button>
					))}
			</Box>

			{!inline && withInternationalName && internationalNames.length > 0 && (
				<Typography variant={variant} noWrap={noWrap}>
					{internationalNames.join(' | ')}
				</Typography>
			)}
		</Box>
	);
};
